import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { consumeHandoffSessionSuccess } from '../stores/handoff-session-store';
import { ConsumedHandoffSession } from '../models/handoffSession';
import { base, createApiResponse, ResponseObj } from '../lib-api/services/base';
import { getUser, registerAnonymousUser, UserResponse } from '../lib-api/services/User';
import { AppThunk } from '../stores/redux-store';
import { setApplicationError } from '../stores/app-state-store';
import { getUserFailure, getUserStart, getUserSuccess } from '../stores/user-auth-store';
import { consumeHandoffSession } from '../lib-api/services/HandoffSession';
import { CommonError } from '../stores/app-state-errors';
import { token as createUserAccessToken } from '../lib-api/services/Authentication';

const throwIfNotOk = (response: ResponseObj<any>, error?: CommonError) => {
  if (response.isOk()) {
    return;
  }

  if (error) {
    throw error;
  }

  throw response;
};

type FetchByAuthorizationCodePayload = {
  clientId: string;
  authorizationCode: string;
};

export const fetchByAuthorizationCode = async ({
  clientId,
  authorizationCode,
}: FetchByAuthorizationCodePayload): Promise<ResponseObj<UserResponse>> => {
  try {
    const accessTokenResponse = await createUserAccessToken({ clientId, authorizationCode });
    throwIfNotOk(accessTokenResponse);

    const { accessToken, idHint } = accessTokenResponse.data;
    base.setAuthorization(accessToken);

    const userResponse = await getUser();
    throwIfNotOk(userResponse);

    return createApiResponse<UserResponse>(
      {
        user: userResponse.data,
        idHint,
        accessToken,
      },
      userResponse.status
    );
  } catch (errorResponse) {
    return errorResponse as ResponseObj<any>;
  }
};

export const fetchByAuthorizationToken = async (
  token: string
): Promise<ResponseObj<UserResponse>> => {
  try {
    base.setAuthorization(token);
    const userResponse = await getUser();
    throwIfNotOk(userResponse);

    return createApiResponse(
      {
        user: userResponse.data,
        accessToken: token,
      },
      userResponse.status
    );
  } catch (errorResponse) {
    return errorResponse as ResponseObj<any>;
  }
};

const obtainByToken = async (token: string): Promise<UserResponse> => {
  const userResponse = await fetchByAuthorizationToken(token);

  throwIfNotOk(userResponse, {
    errorStatus: ErrorStatus.INTERNAL_ERROR,
    errorReason: ErrorReason.REQUEST_FAILED_FETCH_USER_WITH_EXISTING_TOKEN,
  });

  return userResponse.data;
};

const obtainByCode = async (authorizationCode: string, clientId: string): Promise<UserResponse> => {
  const userResponse = await fetchByAuthorizationCode({
    clientId,
    authorizationCode,
  });

  throwIfNotOk(userResponse, {
    errorStatus: ErrorStatus.INTERNAL_ERROR,
    errorReason: ErrorReason.REQUEST_FAILED_FETCH_EXISTING_USER,
  });

  return userResponse.data;
};

const obtainByHandoffSessionId = async (
  handoffSessionId: string
): Promise<{ user: UserResponse; handoff: ConsumedHandoffSession }> => {
  const handoffSessionResponse = await consumeHandoffSession({
    handoffSessionId,
  });

  throwIfNotOk(handoffSessionResponse, {
    errorStatus: ErrorStatus.INTERNAL_ERROR,
    errorReason: ErrorReason.REQUEST_FAILED_FETCH_EXISTING_USER,
  });

  return {
    user: await obtainByToken(handoffSessionResponse.data.userAccessToken),
    handoff: handoffSessionResponse.data,
  };
};

const obtainByRegisteringAnonymousUser = async (): Promise<UserResponse> => {
  const registerResponse = await registerAnonymousUser();

  throwIfNotOk(registerResponse, {
    errorStatus: ErrorStatus.INTERNAL_ERROR,
    errorReason: ErrorReason.REQUEST_FAILED_CREATE_ANONYMOUS_USER,
  });

  return {
    user: registerResponse.data.user,
    accessToken: registerResponse.data.accessToken,
  };
};

type UserProps = {
  authorizationHeader: string | undefined;
  authorizationCode: string | undefined;
  handoffSessionId: string | undefined;
  clientId: string | undefined;
};

export type UserObtainers = {
  byToken: (token: string) => Promise<UserResponse>;
  byCode: (authorizationCode: string, clientId: string) => Promise<UserResponse>;
  byHandoffSessionId: (
    handoffSessionId: string
  ) => Promise<{ user: UserResponse; handoff: ConsumedHandoffSession }>;
  byAnonymousUser: () => Promise<UserResponse>;
};

type ObtainUserProps = UserProps & {
  obtainers?: UserObtainers;
};

export const obtainUser = async ({
  authorizationCode,
  authorizationHeader,
  handoffSessionId,
  clientId,
  obtainers = {
    byToken: obtainByToken,
    byCode: obtainByCode,
    byHandoffSessionId: obtainByHandoffSessionId,
    byAnonymousUser: obtainByRegisteringAnonymousUser,
  },
}: ObtainUserProps): Promise<{ user: UserResponse; handoff?: ConsumedHandoffSession }> => {
  if (authorizationHeader) {
    return { user: await obtainers.byToken(authorizationHeader) };
  }

  if (authorizationCode && clientId) {
    return { user: await obtainers.byCode(authorizationCode, clientId) };
  }

  if (handoffSessionId) {
    return obtainers.byHandoffSessionId(handoffSessionId);
  }

  return { user: await obtainers.byAnonymousUser() };
};

export const initiateUser =
  (userInitiatorProps: UserProps): AppThunk =>
  async (dispatch) => {
    dispatch(getUserStart());

    try {
      const { user, handoff } = await obtainUser({ ...userInitiatorProps });

      dispatch(getUserSuccess(user));

      if (handoff) {
        dispatch(consumeHandoffSessionSuccess(handoff));
      }
    } catch (error) {
      dispatch(getUserFailure());
      dispatch(setApplicationError(error));
    }
  };
